<script setup lang="ts" generic="L extends PejerTableNames">
import { AccessorKeyColumnDef, Cell } from '@tanstack/vue-table'
import { ArrowDown, Copy, Eye, EyeOff, RefreshCcw, Search } from 'lucide-vue-next'
import { getNestedObject } from 'pejer/src/tables/helpers'
import { Order } from 'pejer/src/types/types'
import { PejerTableNames, PejerTablesList } from '../../../types/base'
import { toast } from '../../ui/toast'
import { GroupedVisibleCells } from './types'

const props = defineProps<{
  cell: Cell<PejerTablesList<L>, unknown>
  cellGroup: GroupedVisibleCells<PejerTablesList<L>>
  showColumn: (col: string, show?: boolean) => void
  triggerSearch: (column: string) => void
  shouldHideColumn: (column: string) => boolean
  toggleSortBy: (columnId: keyof PejerTablesList<L>) => void
  sortBy: keyof PejerTablesList<L>
  sorter: Order
  isFetching: boolean
}>()

const copyText = (text: string) => {
  navigator.clipboard.writeText(text)
  toast({
    title: `Kopierte text '${text}'`,
  })
}

const hideGroup = () => {
  props.cellGroup.cells.supporting.forEach((cell) => {
    props.showColumn(cell.column.id, false)
  })

  props.showColumn(props.cellGroup.cells.main.column.id, false)
}
</script>
<template>
  <div class="flex items-center">
    <div
      class="flex items-end absolute right-0 top-1/2 -translate-y-1/2 z-20"
      v-if="(cell.column.columnDef as any).accessorKey"
    >
      <button
        class="hover:bg-red-200 rounded-sm flex-shrink-0 p-[.25rem] transition-opacity duration-200 relative group/inner group-hover:opacity-100 opacity-0 bg-white"
        @click.prevent.stop="
          () => {
            if (cellGroup.cells.main.column.id == cell.column.id) hideGroup()
            else showColumn(cell.column.id, false)
          }
        "
      >
        <div class="w-[14px] h-[14px]"></div>
        <Eye
          class="group-hover/inner:hover:opacity-0 opacity-100 absolute z-10 top-1/2 -translate-y-1/2"
          :size="14"
        />
        <EyeOff
          class="group-hover/inner:hover:opacity-100 opacity-0 absolute z-10 top-1/2 -translate-y-1/2"
          :size="14"
        />
      </button>
      <button
        class="transition-all hover:bg-blue-500 hover:text-white p-[.25rem] rounded-sm w-min group-hover:opacity-100 opacity-0 bg-white"
        @click.prevent.stop="
          copyText(
            getNestedObject(cell.row.original, (cell.column.columnDef as any).accessorKey as string)
          )
        "
      >
        <Copy :size="14" />
      </button>
      <button
        v-if="cell.column.columnDef.meta?.searchable !== false"
        :disabled="shouldHideColumn(cell.column.id)"
        class="transition-all hover:bg-blue-500 hover:text-white p-[.25rem] rounded-sm w-min group-hover:opacity-100 opacity-0 bg-white"
        :class="{
          'cursor-pointer': !shouldHideColumn(cell.column.id),
          'cursor-not-allowed': shouldHideColumn(cell.column.id),
        }"
        @click.prevent.stop="
          () => {
            triggerSearch(
              (cell.column.columnDef as AccessorKeyColumnDef<PejerTablesList<L>, any>)
                .accessorKey as string
            )
          }
        "
      >
        <Search :size="14" />
      </button>
      <button
        v-if="
          cell.column.columnDef.meta?.sortable !== false && 'accessorKey' in cell.column.columnDef
        "
        class="transition-all hover:bg-blue-500 hover:text-white p-[.25rem] rounded-sm w-min group-hover:opacity-100 bg-white/80"
        :class="{
          'opacity-0': cell.column.columnDef.accessorKey !== sortBy,
        }"
        @click.prevent.stop="
          () => {
            toggleSortBy((cell.column.columnDef as any).accessorKey)
          }
        "
      >
        <ArrowDown
          v-if="!isFetching"
          :style="{
            transition: 'all',
            transform: sorter === 'Ascending' ? 'rotate(180deg)' : '',
          }"
          :size="14"
        />
        <RefreshCcw v-else class="animate-spin scale-[-1]" :size="14" />
      </button>
    </div>
  </div>
</template>
